import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, Target, HandCoins, UserRound, History as HistoryIcon, StickyNote, CircleHelp,
  ContactRound, CalendarDays, FileSignature, Compass, Building2, Plus, Pencil,
  RotateCcw, XCircle, Scale, MessageCircle, Phone, Mail, CalendarPlus, ShieldAlert, CheckCircle2, ChevronRight,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { cn, initialsOf, dateLocale } from "@/lib/utils";
import { fmtDate, relDays } from "@/pages/Reperes";
import ConfirmPlacementDialog from "@/components/placements/ConfirmPlacementDialog";
import { translateFr as tfr } from "@/i18n";
import {
  CareerProjectDialog, TargetClubDialog, OutreachLogDialog, OfferDialog, FeedbackDialog,
  CompareOffersDialog, TerminateDialog, CheckinDialog, ScheduleCheckinDialog,
} from "@/components/mandates/MandateDialogs";

export const TARGET_FR = { TARGETED: tfr("Ciblé"), PROPOSED: tfr("Proposé"), INTERESTED: tfr("Intéressé"), TRIAL: tfr("Essai"), OFFER_RECEIVED: tfr("Offre reçue"), REJECTED: tfr("Refus") };
export const OFFER_ACTIVE = ["RECEIVED", "UNDER_REVIEW", "COUNTERED"];

function Card({ title, Icon, action, children, testid, onClick }) {
  return (
    <section onClick={onClick} className={cn("rounded-xl border border-border bg-card p-4 xl:p-3.5 min-w-0", onClick && "cursor-pointer hover:border-foreground/25 transition-colors")} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 text-xs xl:text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">
          {Icon && <Icon className="h-3.5 w-3.5" />}{title}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function MandateDetail({ c, tf, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("overview");
  const [dlg, setDlg] = useState(null);
  const [history, setHistory] = useState(null);

  const loadDetail = useCallback(async () => {
    try { setDetail((await api.get(`/mandats-signes/${c.id}`)).data); } catch { /* row stays usable */ }
  }, [c.id]);
  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    api.get("/dashboard/activity", { params: { player_id: c.id, limit: 25 } })
      .then((r) => setHistory(r.data.items || [])).catch(() => setHistory([]));
  }, [c.id, detail]);

  const d = detail?.athlete || c;
  const mandate = detail?.mandate ?? c.mandate;
  const project = detail?.career_project || null;
  const targets = detail?.target_clubs || [];
  const offers = detail?.offers || [];
  const activeOffers = offers.filter((o) => OFFER_ACTIVE.includes(o.status));
  const contacts = detail?.contacts || [];
  const notes = detail?.notes || [];
  const ex = detail?.last_exchange || null;
  const state = d.client_state;
  const refresh = () => { loadDetail(); onChanged(); };

  const renew = async () => {
    try {
      const r = await api.post(`/mandats-signes/${c.id}/renew`);
      toast.success(tf("Dossier de renouvellement prêt"));
      navigate(`/contracts/renewals/${r.data.renewal_case_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };
  const reactivate = async () => {
    try {
      const r = await api.post(`/mandats-signes/${c.id}/reactivate`);
      toast.success(tf("Nouveau mandat en préparation"));
      navigate(`/contracts/${r.data.contract_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };

  const PRIMARY = {
    TO_LAUNCH: { label: tfr("Définir le projet de carrière"), icon: Compass, run: () => setDlg({ type: "project" }), testid: "action-define-project" },
    SEARCHING: { label: tfr("Proposer à un club"), icon: Send, run: () => setDlg({ type: targets.length ? "outreach-pick" : "add-target" }), testid: "action-propose-club" },
    NEGOTIATING: activeOffers.length >= 2
      ? { label: tfr("Comparer les offres"), icon: Scale, run: () => setDlg({ type: "compare" }), testid: "action-compare-offers" }
      : { label: tfr("Confirmer le placement"), icon: CheckCircle2, run: () => setDlg({ type: "pick-offer-placement" }), testid: "action-confirm-placement" },
    RENEWAL_DUE: { label: tfr("Renouveler le mandat"), icon: RotateCcw, run: renew, testid: "action-renew" },
    ENDED: { label: tfr("Voir l'historique"), icon: HistoryIcon, run: () => setTab("history"), testid: "action-history" },
  }[state];

  const TABS = [
    { id: "overview", label: tfr("Vue d'ensemble"), Icon: CircleHelp },
    { id: "mandate", label: tfr("Mandat"), Icon: FileSignature },
    { id: "project", label: tfr("Projet de carrière"), Icon: Compass },
    { id: "clubs", label: tfr("Clubs ciblés"), Icon: Building2, count: targets.length },
    { id: "offers", label: "Offres", Icon: HandCoins, count: offers.length },
    { id: "notes", label: "Notes", Icon: StickyNote, count: notes.length },
    { id: "history", label: "Historique", Icon: HistoryIcon },
  ];

  return (
    <div className="rounded-2xl border-0 bg-transparent p-0 xl:border xl:border-border xl:bg-background/40 xl:p-4 space-y-5 xl:space-y-4 min-w-0" data-testid="mandate-detail">
      {/* Identity */}
      <div className="flex items-start gap-3.5 xl:gap-3 min-w-0 pr-11 xl:pr-0">
        <Avatar className="h-16 w-16 xl:h-14 xl:w-14 shrink-0">
          <AvatarImage src={d.photo_url} alt="" />
          <AvatarFallback>{initialsOf(d.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[22px] leading-tight xl:text-lg font-bold truncate" data-testid="detail-name">{d.name}</span>
            {d.minor && <span className="text-[10px] font-semibold uppercase rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px" data-testid="detail-minor-badge">{tf("Mineur")}</span>}
            <span className={cn("text-[10px] rounded-full border px-1.5 py-px font-medium",
              { SEARCHING: "border-info/40 bg-info/10 text-info", NEGOTIATING: "border-info/40 bg-info/10 text-info",
                RENEWAL_DUE: "border-warning/40 bg-warning/10 text-warning" }[d.client_state]
              || "border-border bg-secondary/60 text-muted-foreground")} data-testid="detail-state-badge">{tf(d.client_state_label || "")}</span>
          </div>
          <div className="text-[15px] xl:text-xs text-muted-foreground truncate">
            {[d.age ? `${d.age} ans` : null, d.position, d.level, d.club || tf("Sans club")].filter(Boolean).join(" · ")}
          </div>
          {mandate?.end_date && (
            <div className="text-[13px] xl:text-[11px] text-muted-foreground mt-1.5 xl:mt-1" data-testid="detail-mandate-line">
              {tf("Mandat")} : {fmtDate(mandate.start_date)} → {fmtDate(mandate.end_date)} · {tf(mandate.remaining_label || "")}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2" data-testid="detail-actions">
        {PRIMARY && (
          <Button size="sm" onClick={PRIMARY.run} data-testid={PRIMARY.testid}
            className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3 bg-foreground hover:bg-foreground/90 text-background">
            <PRIMARY.icon className="h-3.5 w-3.5 mr-1.5" />{tf(PRIMARY.label)}
          </Button>
        )}
        {state !== "ENDED" && (
          <>
            <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3" onClick={() => setDlg({ type: "checkin" })} data-testid="action-checkin">
              <MessageCircle className="h-3.5 w-3.5 mr-1.5" />{tf("Ajouter un échange")}
            </Button>
            <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3" onClick={() => setDlg({ type: "schedule-checkin" })} data-testid="action-schedule-checkin">
              <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />{tf("Programmer un point")}
            </Button>
            <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3 text-athlynx border-athlynx/30" onClick={() => setDlg({ type: "terminate" })} data-testid="action-terminate">
              <XCircle className="h-3.5 w-3.5 mr-1.5" />{tf("Résilier")}
            </Button>
          </>
        )}
        {state === "ENDED" && (
          <Button variant="outline" size="sm" className="text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3" onClick={reactivate} data-testid="action-reactivate">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{tf("Réactiver (nouveau mandat)")}
          </Button>
        )}
        <Button variant="ghost" size="sm"
          className="w-full xl:w-auto justify-start h-14 xl:h-9 text-sm xl:text-xs text-foreground xl:text-muted-foreground border-y border-border/60 xl:border-0 rounded-none xl:rounded-md px-1 xl:px-3"
          onClick={() => navigate(`/players/${c.id}`)} data-testid="detail-open-profile">
          <UserRound className="h-4 w-4 xl:h-3.5 xl:w-3.5 mr-2 xl:mr-1.5" />{tf("Voir le profil")}
          <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground xl:hidden" aria-hidden="true" />
        </Button>
      </div>

      {state === "ENDED" && (
        <div className="rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-xs text-muted-foreground" data-testid="ended-banner">
          {tf("Mandat terminé")}{d.mandate_ended_at ? ` ${relDays(d.mandate_ended_at, tf)}` : ""}.
          {" "}{tf("La réactivation démarre un nouveau mandat — l'athlète redeviendra actif une fois le nouveau mandat signé.")}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto scroll-thin" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            data-testid={`detail-tab-${t.id}`}
            className={cn("shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm xl:px-3 xl:py-2 xl:text-xs font-medium border-b-2 -mb-px transition-colors",
              tab === t.id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            <t.Icon className="h-3.5 w-3.5" />{tf(t.label)}
            {t.count > 0 && <span className="text-[10px] tabular-nums rounded-full border border-border px-1.5">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab d={d} tf={tf} mandate={mandate} project={project} targets={targets}
        offers={offers} ex={ex} nextEvent={detail?.next_event} contacts={contacts} setTab={setTab} setDlg={setDlg} />}
      {tab === "mandate" && <MandateTab tf={tf} mandate={mandate} history={detail?.mandate_history || []}
        renewal={detail?.renewal_case} onEdit={() => setDlg({ type: "mandate-edit" })} onRenew={renew} navigate={navigate} />}
      {tab === "project" && <ProjectTab tf={tf} project={project} minor={d.minor} onEdit={() => setDlg({ type: "project" })} />}
      {tab === "clubs" && <ClubsTab tf={tf} targets={targets} setDlg={setDlg} pid={c.id} refresh={refresh} />}
      {tab === "offers" && <OffersTab tf={tf} offers={offers} activeOffers={activeOffers} setDlg={setDlg} />}
      {tab === "notes" && <NotesTab pid={c.id} tf={tf} notes={notes} onSaved={loadDetail} />}
      {tab === "history" && <HistoryTab tf={tf} history={history} />}

      {/* Dialogs */}
      {dlg?.type === "project" && <CareerProjectDialog pid={c.id} project={project} minor={d.minor} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "add-target" && <TargetClubDialog pid={c.id} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {(dlg?.type === "outreach" || dlg?.type === "outreach-pick") && (
        <OutreachLogDialog pid={c.id} targets={targets} target={dlg.target} tf={tf}
          onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />
      )}
      {dlg?.type === "offer" && <OfferDialog pid={c.id} targets={targets} offer={dlg.offer} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "feedback" && <FeedbackDialog pid={c.id} offer={dlg.offer} minor={d.minor} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {(dlg?.type === "compare") && <CompareOffersDialog offers={activeOffers} tf={tf} onClose={() => setDlg(null)}
        onPlace={(offer) => setDlg({ type: "placement", offer })} />}
      {dlg?.type === "pick-offer-placement" && (
        activeOffers.length === 1
          ? <PlacementFlow pid={c.id} player={d} offer={activeOffers[0]} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />
          : <CompareOffersDialog offers={activeOffers} tf={tf} onClose={() => setDlg(null)} onPlace={(offer) => setDlg({ type: "placement", offer })} />
      )}
      {dlg?.type === "placement" && <PlacementFlow pid={c.id} player={d} offer={dlg.offer} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "terminate" && <TerminateDialog pid={c.id} name={d.name} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "checkin" && <CheckinDialog pid={c.id} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "schedule-checkin" && <ScheduleCheckinDialog pid={c.id} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
      {dlg?.type === "mandate-edit" && <MandateEditInline pid={c.id} mandate={mandate} tf={tf} onClose={() => setDlg(null)} onDone={() => { setDlg(null); refresh(); }} />}
    </div>
  );
}

function PlacementFlow({ pid, player, offer, tf, onClose, onDone }) {
  const [accepted, setAccepted] = useState(false);
  useEffect(() => {
    api.post(`/mandats-signes/${pid}/offers/${offer.id}/accept`)
      .then(() => setAccepted(true))
      .catch((e) => { toast.error(e?.response?.data?.detail || tf("Erreur")); onClose(); });
  }, [pid, offer.id, tf, onClose]);
  if (!accepted) return null;
  return (
    <ConfirmPlacementDialog open onOpenChange={(o) => { if (!o) onClose(); }}
      player={{ ...player, club: offer.club_name }} onConfirmed={() => { toast.success(tf("Placement confirmé")); onDone(); }} />
  );
}

function OverviewTab({ d, tf, mandate, project, targets, offers, ex, nextEvent, contacts, setTab, setDlg }) {
  const dl = d.next_deadline;
  const obj = project?.objectives || {};
  const zones = project?.preferences?.zones || [];
  return (
    <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-4" data-testid="detail-overview">
      <Card title={tf("Fin du mandat")} Icon={CalendarDays} testid="card-mandate-end">
        {mandate?.end_date ? (
          <div className="text-sm xl:text-xs space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-bold tabular-nums">{fmtDate(mandate.end_date)}</span>
              <span className={cn("text-[11px] font-medium", (mandate.days_left ?? 999) <= 90 ? "text-athlynx" : "text-muted-foreground")}>{tf(mandate.remaining_label || "")}</span>
            </div>
            {mandate.commission_percent != null && <div className="text-muted-foreground">{tf("Commission")} : {mandate.commission_percent}%{mandate.commission_payer ? ` · ${tf("payée par")} ${mandate.commission_payer}` : ""}</div>}
          </div>
        ) : <p className="text-sm xl:text-xs text-muted-foreground">{tf("Document du mandat non ajouté.")}</p>}
      </Card>

      <Card title={tf("Club actuel")} Icon={Building2} testid="card-current-club">
        <div className="text-sm xl:text-xs space-y-1">
          <div className="text-sm font-bold">{d.club || tf("Sans club")}</div>
          {d.level && <div className="text-muted-foreground">{d.level}</div>}
          {d.contract_end_date && <div className="text-muted-foreground">{tf("Fin de contrat")} : {fmtDate(d.contract_end_date)} <span className="text-[10px] rounded-full border border-border px-1.5 py-px ml-1">{tf(d.contract_remaining_label || "")}</span></div>}
        </div>
      </Card>

      <Card title={tf("Clubs approchés")} Icon={Target} testid="card-clubs-targeted" onClick={() => setTab("clubs")}>
        <div className="text-sm font-bold tabular-nums">{targets.length}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {targets.filter((t) => t.status === "INTERESTED").length} {tf("intéressé(s)")} · {targets.filter((t) => t.status === "OFFER_RECEIVED").length} {tf("offre(s)")}
        </div>
      </Card>

      <Card title={tf("Offres reçues")} Icon={HandCoins} testid="card-offers" onClick={() => setTab("offers")}>
        <div className="text-sm font-bold tabular-nums">{offers.length}</div>
        {offers.length === 0 && <p className="text-[11px] text-muted-foreground mt-0.5">{tf("Aucune offre reçue.")}</p>}
      </Card>

      <Card title={tf("Prochaine échéance")} Icon={CalendarDays} testid="card-next-deadline">
        {dl ? (
          <div className="text-sm xl:text-xs space-y-1">
            <div className="font-semibold">{tf(dl.label)}</div>
            <div className="flex items-baseline gap-2">
              <span className="tabular-nums">{fmtDate(dl.date)}</span>
              <span className={cn("text-[11px] font-medium", dl.days <= 7 ? "text-athlynx" : dl.days <= 30 ? "text-warning" : "text-muted-foreground")}>
                {dl.days === 0 ? tf("aujourd'hui") : `${tf("dans")} ${dl.days} j`}
              </span>
            </div>
          </div>
        ) : <p className="text-sm xl:text-xs text-muted-foreground">—</p>}
      </Card>

      <Card title={tf("Dernier échange")} Icon={MessageCircle} testid="card-last-exchange">
        {ex ? (
          <div className="text-sm xl:text-xs space-y-1">
            <div className="font-semibold">{{ whatsapp: "WhatsApp", email: "Email", call: tfr("Point téléphonique"), meeting: "Rendez-vous", video: "Visio" }[ex.type] || tf("Échange")}</div>
            <div className="text-muted-foreground tabular-nums">{fmtDate(ex.occurred_at)} · {relDays(ex.occurred_at, tf)}</div>
            {ex.note && <p className="text-foreground/85 italic line-clamp-2 border-l-2 border-border pl-2 mt-1">« {ex.note} »</p>}
          </div>
        ) : <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun échange enregistré.")}</p>}
      </Card>

      <Card title={tf("Objectif principal")} Icon={Compass} testid="card-objective" onClick={() => setTab("project")}>
        {obj.target_level ? (
          <div className="text-xs space-y-0.5">
            <div className="font-semibold">{obj.target_level}</div>
            {obj.playing_time && <div className="text-muted-foreground">{obj.playing_time}</div>}
          </div>
        ) : <p className="text-sm xl:text-xs text-muted-foreground">{tf("Le projet de carrière n'a pas encore été défini.")}</p>}
      </Card>

      <Card title={tf("Zones recherchées")} Icon={Target} testid="card-zones">
        {zones.length ? (
          <div className="flex flex-wrap gap-1.5">
            {zones.slice(0, 4).map((z) => <span key={z} className="text-[11px] rounded-full border border-border px-2 py-0.5">{z}</span>)}
            {zones.length > 4 && <span className="text-[11px] rounded-full border border-border px-2 py-0.5">+{zones.length - 4}</span>}
          </div>
        ) : <p className="text-sm xl:text-xs text-muted-foreground">—</p>}
      </Card>

      <Card title={tf("Interlocuteurs")} Icon={ContactRound} testid="card-interlocutors">
        {d.minor && !contacts.some((x) => x.is_legal_representative) && (
          <div className="rounded-lg border border-warning/30 bg-warning/[0.06] p-2 mb-2 text-[11px] text-warning flex items-center gap-1.5" data-testid="no-legal-rep-warning">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />{tf("Aucun représentant légal renseigné.")}
          </div>
        )}
        {contacts.length === 0 ? <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun interlocuteur renseigné.")}</p> : (
          <ul className="space-y-2">
            {contacts.slice(0, 3).map((x) => (
              <li key={x.id} className="flex items-start justify-between gap-2 min-w-0" data-testid={`contact-${x.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold">{x.name}</span>
                    {x.is_legal_representative && <span className="text-[10px] font-semibold rounded-full border border-warning/40 bg-warning/10 text-warning px-1.5 py-px">{tf("Représentant légal")}</span>}
                  </div>
                  {x.relationship && <div className="text-[11px] text-muted-foreground">{tf(x.relationship)}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {x.phone && <a href={`tel:${x.phone}`} className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60" aria-label={`${tf("Appeler")} ${x.name}`}><Phone className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                  {x.email && <a href={`mailto:${x.email}`} className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border hover:bg-secondary/60" aria-label={`Email ${x.name}`}><Mail className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {nextEvent && (
        <Card title={tf("Prochain événement")} Icon={CalendarPlus} testid="card-next-event">
          <div className="text-xs space-y-0.5">
            <div className="font-semibold">{nextEvent.title}</div>
            <div className="text-muted-foreground tabular-nums">{fmtDate(nextEvent.starts_at)}{nextEvent.location ? ` · ${nextEvent.location}` : ""}</div>
          </div>
        </Card>
      )}
    </div>
  );
}

function MandateTab({ tf, mandate, history, renewal, onEdit, onRenew, navigate }) {
  const Row = ({ k, v }) => (
    <div className="flex items-start justify-between gap-3 text-sm xl:text-xs">
      <dt className="text-muted-foreground shrink-0">{k}</dt>
      <dd className="text-right font-medium min-w-0">{v ?? "—"}</dd>
    </div>
  );
  const SCOPE_FR = { representation: tfr("Représentation sportive"), club_negotiations: tfr("Négociations clubs"), sponsorship: "Sponsoring", image_rights: tfr("Droits à l'image"), other: "Autre" };
  return (
    <div className="space-y-4" data-testid="detail-mandate-tab">
      {mandate ? (
        <Card title={tf("Mandat de représentation")} Icon={FileSignature} testid="card-mandate"
          action={<button onClick={onEdit} className="text-muted-foreground hover:text-foreground" aria-label={tf("Modifier le mandat")} data-testid="edit-mandate-btn"><Pencil className="h-3.5 w-3.5" /></button>}>
          <dl className="space-y-1.5">
            <Row k={tf("Statut")} v={{ active: "Actif", signed: tfr("Signé"), partially_signed: tfr("Partiellement signé"), pending_signature: "En signature", draft: tfr("Brouillon"), terminated: tfr("Résilié"), expired: tfr("Expiré") }[mandate.status] || mandate.status} />
            <Row k={tf("Début")} v={fmtDate(mandate.start_date)} />
            <Row k={tf("Fin")} v={mandate.end_date ? `${fmtDate(mandate.end_date)} (${tf(mandate.remaining_label || "")})` : null} />
            <Row k={tf("Commission")} v={mandate.commission_percent != null ? `${mandate.commission_percent}%` : null} />
            <Row k={tf("Payée par")} v={mandate.commission_payer} />
            <Row k={tf("Exclusivité")} v={mandate.exclusive === true ? tf("Oui") : mandate.exclusive === false ? tf("Non") : null} />
            <Row k={tf("Périmètre")} v={(mandate.scope || []).length ? mandate.scope.map((s) => tf(SCOPE_FR[s] || s)).join(", ") : null} />
            <Row k={tf("Territoire")} v={mandate.territory} />
            {mandate.termination_reason && <Row k={tf("Fin de mandat")} v={`${fmtDate(mandate.termination_date)} — ${tf({ expiration: "Expiration", athlete: tfr("Résiliation par l'athlète"), agent: tfr("Résiliation par l'agent"), mutual: tfr("Accord mutuel"), other: "Autre" }[mandate.termination_reason] || mandate.termination_reason)}`} />}
          </dl>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate(`/contracts/${mandate.id}`)} data-testid="open-mandate-contract">
              {tf("Ouvrir le contrat / signature")}
            </Button>
            {["active", "signed"].includes(mandate.status) && (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onRenew} data-testid="mandate-renew-btn">
                <RotateCcw className="h-3 w-3 mr-1" />{tf("Renouveler le mandat")}
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card title={tf("Mandat de représentation")} Icon={FileSignature} testid="card-mandate">
          <p className="text-sm xl:text-xs text-muted-foreground">{tf("Aucun mandat actif. Créez un contrat de type « Mandat de représentation » depuis Contrats.")}</p>
          <Button variant="outline" size="sm" className="h-8 text-xs mt-2" onClick={() => navigate("/contracts")} data-testid="goto-contracts">{tf("Ouvrir Contrats")}</Button>
        </Card>
      )}
      {renewal && (
        <Card title={tf("Renouvellement (Renewal Intelligence)")} Icon={RotateCcw} testid="card-renewal">
          <div className="text-sm xl:text-xs space-y-1">
            <div className="font-semibold">{renewal.status_label}</div>
            {renewal.contract_end_date && <div className="text-muted-foreground">{tf("Échéance")} : {fmtDate(renewal.contract_end_date)}</div>}
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs mt-2" onClick={() => navigate(`/contracts/renewals/${renewal.id}`)} data-testid="open-renewal-case">
            {tf("Ouvrir le dossier de renouvellement")}
          </Button>
        </Card>
      )}
      {history.length > 0 && (
        <Card title={tf("Mandats précédents")} Icon={HistoryIcon} testid="card-mandate-history">
          <ul className="space-y-2">
            {history.map((m) => (
              <li key={m.id} className="text-xs flex items-center justify-between gap-2">
                <span>{fmtDate(m.start_date)} → {fmtDate(m.end_date)}</span>
                <span className="text-muted-foreground">{{ terminated: tfr("Résilié"), expired: tfr("Expiré"), archived: tfr("Archivé"), cancelled: tfr("Annulé"), draft: tfr("Brouillon") }[m.status] || m.status}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function ProjectTab({ tf, project, minor, onEdit }) {
  const Block = ({ title, rows }) => (
    <Card title={title} Icon={Compass}>
      <dl className="space-y-1.5">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-3 text-sm xl:text-xs">
            <dt className="text-muted-foreground shrink-0">{k}</dt>
            <dd className="text-right font-medium min-w-0">{v}</dd>
          </div>
        ))}
        {!rows.some(([, v]) => v) && <p className="text-sm xl:text-xs text-muted-foreground">—</p>}
      </dl>
    </Card>
  );
  if (!project) {
    return (
      <div className="rounded-xl border border-dashed border-border py-10 text-center space-y-3" data-testid="project-empty">
        <p className="text-sm text-muted-foreground">{tf("Le projet de carrière n'a pas encore été défini.")}</p>
        <Button size="sm" className="bg-foreground hover:bg-foreground/90 text-background text-sm h-12 px-4 xl:text-xs xl:h-9 xl:px-3" onClick={onEdit} data-testid="project-define-btn">
          <Compass className="h-3.5 w-3.5 mr-1.5" />{tf("Définir le projet de carrière")}
        </Button>
      </div>
    );
  }
  const o = project.objectives || {}, p = project.preferences || {}, cs = project.constraints || {};
  return (
    <div className="space-y-4" data-testid="detail-project-tab">
      <div className="flex items-center justify-between gap-2">
        <span className={cn("text-[11px] rounded-full border px-2 py-0.5 font-medium",
          project.status === "validated" ? "border-success/40 bg-success/10 text-success" : "border-border text-muted-foreground")} data-testid="project-status-badge">
          {project.status === "validated"
            ? `${tf("Validé")}${project.validated_by ? ` ${tf("par")} ${project.validated_by}` : ""}${minor ? ` (${tf("représentant légal")})` : ""}`
            : tf("Brouillon")}
        </span>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onEdit} data-testid="project-edit-btn">
          <Pencil className="h-3 w-3 mr-1" />{tf("Modifier")}
        </Button>
      </div>
      <div className="grid grid-cols-1 min-[1700px]:grid-cols-2 gap-4">
        <Block title={tf("Objectifs")} rows={[[tf("Niveau visé"), o.target_level], [tf("Type de club"), o.target_club_type], [tf("Temps de jeu"), o.playing_time], [tf("Court terme"), o.short_term], [tf("Moyen terme"), o.medium_term]]} />
        <Block title={tf("Préférences")} rows={[[tf("Zones"), (p.zones || []).join(", ") || null], [tf("Salaire visé"), p.salary_min || p.salary_max ? `${p.salary_min ?? "?"} – ${p.salary_max ?? "?"} € / mois` : null], [tf("Durée de contrat"), p.contract_duration], [tf("Compétitions"), p.competitions]]} />
        <Block title={tf("Contraintes")} rows={[[tf("Famille"), cs.family], [tf("Études"), cs.studies], [tf("Mobilité"), cs.relocation], [tf("Langue"), cs.language], [tf("Autre"), cs.other]]} />
      </div>
    </div>
  );
}

function ClubsTab({ tf, targets, setDlg, pid, refresh }) {
  const removeTarget = async (tid) => {
    try { await api.delete(`/mandats-signes/${pid}/target-clubs/${tid}`); toast.success(tf("Club retiré")); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
  };
  const STATUS_CLS = {
    TARGETED: "border-border text-muted-foreground", PROPOSED: "border-info/40 bg-info/10 text-info",
    INTERESTED: "border-success/40 bg-success/10 text-success", TRIAL: "border-warning/40 bg-warning/10 text-warning",
    OFFER_RECEIVED: "border-athlynx/40 bg-athlynx/10 text-athlynx", REJECTED: "border-border text-muted-foreground/70",
  };
  return (
    <div className="space-y-3" data-testid="detail-clubs-tab">
      <div className="flex justify-end">
        <Button size="sm" className="bg-foreground hover:bg-foreground/90 text-background text-xs h-8" onClick={() => setDlg({ type: "add-target" })} data-testid="add-target-club-btn">
          <Plus className="h-3 w-3 mr-1" />{tf("Ajouter un club cible")}
        </Button>
      </div>
      {targets.length === 0 ? (
        <p className="text-xs text-muted-foreground py-8 text-center" data-testid="clubs-empty">{tf("Aucun club ciblé pour le moment.")}</p>
      ) : (
        <ul className="space-y-2.5">
          {targets.map((t) => (
            <li key={t.id} className="rounded-xl border border-border bg-card p-3 min-w-0" data-testid={`target-${t.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{t.club_name}</span>
                    <span className={cn("text-[10px] rounded-full border px-1.5 py-px font-medium", STATUS_CLS[t.status])} data-testid={`target-status-${t.id}`}>{tf(t.status_label)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {[t.league, t.country, t.contact ? `${t.contact.name}${t.contact.role ? ` (${t.contact.role})` : ""}` : null].filter(Boolean).join(" · ")}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                    {t.last_interaction_at ? `${tf("Dernier échange")} ${relDays(t.last_interaction_at, tf)}` : tf("Aucun échange")}
                    {t.next_follow_up_at ? ` · ${tf("Relance")} ${fmtDate(t.next_follow_up_at)}` : ""}
                    {t.attempts ? ` · ${t.attempts} ${tf("démarche(s)")}` : ""}
                  </div>
                  {t.notes && <p className="text-[11px] text-foreground/80 italic mt-1">« {t.notes} »</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!["REJECTED", "OFFER_RECEIVED"].includes(t.status) && (
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "outreach", target: t })} data-testid={`target-outreach-${t.id}`}>
                      <Send className="h-3 w-3 mr-1" />{tf("Proposer")}
                    </Button>
                  )}
                  {["PROPOSED", "INTERESTED", "TRIAL"].includes(t.status) && (
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "offer", offer: null, targetPreset: t })} data-testid={`target-offer-${t.id}`}>
                      <HandCoins className="h-3 w-3 mr-1" />{tf("Offre")}
                    </Button>
                  )}
                  <button onClick={() => removeTarget(t.id)} className="h-7 w-7 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-athlynx" aria-label={tf("Retirer")} data-testid={`target-remove-${t.id}`}>
                    <XCircle className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OffersTab({ tf, offers, activeOffers, setDlg }) {
  return (
    <div className="space-y-3" data-testid="detail-offers-tab">
      <div className="flex justify-end gap-2 flex-wrap">
        {activeOffers.length >= 2 && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setDlg({ type: "compare" })} data-testid="compare-offers-btn">
            <Scale className="h-3 w-3 mr-1" />{tf("Comparer les offres")}
          </Button>
        )}
        <Button size="sm" className="bg-foreground hover:bg-foreground/90 text-background text-xs h-8" onClick={() => setDlg({ type: "offer", offer: null })} data-testid="add-offer-btn">
          <Plus className="h-3 w-3 mr-1" />{tf("Enregistrer une offre")}
        </Button>
      </div>
      {offers.length === 0 ? (
        <p className="text-xs text-muted-foreground py-8 text-center" data-testid="offers-empty">{tf("Aucune offre reçue.")}</p>
      ) : (
        <ul className="space-y-2.5">
          {offers.map((o) => (
            <li key={o.id} className="rounded-xl border border-border bg-card p-3 min-w-0" data-testid={`offer-${o.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{o.club_name}</span>
                    <span className={cn("text-[10px] rounded-full border px-1.5 py-px font-medium",
                      o.status === "ACCEPTED" ? "border-success/40 bg-success/10 text-success"
                        : OFFER_ACTIVE.includes(o.status) ? "border-info/40 bg-info/10 text-info"
                          : "border-border text-muted-foreground")} data-testid={`offer-status-${o.id}`}>{tf(o.status_label)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                    {[o.duration_months ? `${o.duration_months} ${tf("mois")}` : null,
                      o.salary_annual ? `${Math.round(o.salary_annual / 12).toLocaleString(dateLocale())} €/mois` : null,
                      o.signing_bonus ? `${tf("Prime")} ${o.signing_bonus.toLocaleString(dateLocale())} €` : null,
                      o.commission_percent != null ? `${tf("Commission")} ${o.commission_percent}%` : null].filter(Boolean).join(" · ")}
                  </div>
                  {o.deadline && (
                    <div className={cn("text-[11px] mt-0.5 tabular-nums font-medium", (o.deadline_days ?? 99) <= 7 ? "text-athlynx" : "text-muted-foreground")}>
                      {tf("Échéance")} : {fmtDate(o.deadline)}{o.deadline_days != null ? ` · ${o.deadline_days === 0 ? tf("aujourd'hui") : `${tf("dans")} ${o.deadline_days} j`}` : ""}
                    </div>
                  )}
                  {(o.feedback || []).length > 0 && (
                    <div className="text-[11px] text-foreground/80 mt-1">
                      {o.feedback.slice(-1).map((f) => (
                        <span key={f.id}>💬 {f.who} — {tf({ interested: tfr("Intéressé"), to_discuss: tfr("À discuter"), refused: tfr("Refus"), preferred: tfr("Préférence n°1"), waiting: tfr("En attente") }[f.verdict] || f.verdict)}{f.note ? ` : « ${f.note} »` : ""}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                  {OFFER_ACTIVE.includes(o.status) && (
                    <>
                      <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "feedback", offer: o })} data-testid={`offer-feedback-${o.id}`}>
                        {tf("Avis athlète")}
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setDlg({ type: "offer", offer: o })} data-testid={`offer-edit-${o.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" className="h-7 text-[11px] bg-foreground hover:bg-foreground/90 text-background" onClick={() => setDlg({ type: "placement", offer: o })} data-testid={`offer-place-${o.id}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" />{tf("Confirmer le placement")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NotesTab({ pid, tf, notes, onSaved }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/interactions", { player_id: pid, type: "note", note: text.trim() });
      setText(""); toast.success(tf("Note ajoutée")); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="detail-notes-tab">
      <textarea value={text} onChange={(e) => setText(e.target.value)}
        placeholder={tf("Ajouter une note sur ce client…")}
        className="w-full rounded-lg border border-border bg-background text-xs min-h-[70px] p-2.5 focus:outline-none focus:ring-1 focus:ring-athlynx/50" data-testid="note-input" />
      <Button size="sm" disabled={busy || !text.trim()} onClick={save}
        className="bg-foreground hover:bg-foreground/90 text-background text-xs h-8" data-testid="note-save-btn">{tf("Enregistrer la note")}</Button>
      {notes.map((n) => (
        <div key={n.id} className="rounded-xl border border-border bg-card p-3 text-xs">
          <p className="text-foreground/90 whitespace-pre-wrap">{n.note}</p>
          <p className="text-[10px] text-muted-foreground mt-1.5">{fmtDate(n.occurred_at)} · {relDays(n.occurred_at, tf)}</p>
        </div>
      ))}
    </div>
  );
}

function HistoryTab({ tf, history }) {
  return (
    <div className="space-y-2" data-testid="detail-history">
      {!history ? <div className="h-20 rounded-xl bg-secondary/60 animate-pulse" /> :
        history.length === 0 ? <p className="text-xs text-muted-foreground py-6 text-center">{tf("Aucun événement dans l'historique.")}</p> : (
          <ul className="space-y-2.5">
            {history.map((h) => (
              <li key={h.id} className="flex items-start gap-2.5 text-xs">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-foreground/50 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground/90">{h.title}</p>
                  {h.subtitle && <p className="text-[11px] text-muted-foreground">{h.subtitle}</p>}
                  <p className="text-[10px] text-muted-foreground/70">{fmtDate(h.at)} · {relDays(h.at, tf)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

function MandateEditInline({ pid, mandate, tf, onClose, onDone }) {
  const [form, setForm] = useState({
    commission_percent: mandate?.commission_percent ?? "", commission_payer: mandate?.commission_payer || "",
    exclusive: mandate?.exclusive ?? true, territory: mandate?.territory || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/mandats-signes/${pid}/mandate`, {
        commission_percent: form.commission_percent === "" ? null : Number(form.commission_percent),
        commission_payer: form.commission_payer, exclusive: form.exclusive, territory: form.territory,
      });
      toast.success(tf("Mandat mis à jour")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="mandate-edit-dialog">
        <h3 className="text-sm font-bold">{tf("Modifier le mandat")}</h3>
        <label className="block text-xs">
          <span className="text-muted-foreground">{tf("Commission (%)")}</span>
          <input type="number" min="0" max="100" step="0.5" value={form.commission_percent}
            onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
            className="mt-1 w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs" data-testid="mandate-commission-input" />
        </label>
        <label className="block text-xs">
          <span className="text-muted-foreground">{tf("Commission payée par")}</span>
          <input value={form.commission_payer} onChange={(e) => setForm({ ...form, commission_payer: e.target.value })}
            placeholder={tf("Club / Athlète…")} className="mt-1 w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs" data-testid="mandate-payer-input" />
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={!!form.exclusive} onChange={(e) => setForm({ ...form, exclusive: e.target.checked })} className="accent-[#E11D48]" data-testid="mandate-exclusive-check" />
          {tf("Mandat exclusif")}
        </label>
        <label className="block text-xs">
          <span className="text-muted-foreground">{tf("Territoire")}</span>
          <input value={form.territory} onChange={(e) => setForm({ ...form, territory: e.target.value })}
            className="mt-1 w-full h-9 rounded-lg border border-border bg-background px-2.5 text-xs" data-testid="mandate-territory-input" />
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="h-8 text-xs bg-foreground hover:bg-foreground/90 text-background" onClick={save} data-testid="mandate-edit-save">{tf("Enregistrer")}</Button>
        </div>
      </div>
    </div>
  );
}
